<template>
  <div :dir="currentLanguage === 'ar' ? 'rtl' : 'ltr'">
    <header class="site-header">
      <div class="container header-inner">
        <a href="https://splendapp.com" class="brand">
          <img src="@/assets/Splendapp-web.svg" alt="splendapp logo" />

        </a>
        <nav class="nav" :aria-label="t.navAria">
          <a href="#calculator" @click.prevent="scrollToElement('calculator')">{{ t.navTool }}</a>
          <a href="#results" @click.prevent="scrollToElement('results')">{{ t.navResult }}</a>
          <a href="#splendapp" @click.prevent="scrollToElement('splendapp')">{{ t.navApps }}</a>
          <a href="#faq" @click.prevent="scrollToElement('faq')">{{ t.navFaq }}</a>
          <a href="#" @click.prevent="toggleLanguage" class="lang-toggle">
            {{ currentLanguage === 'en' ? 'عربي' : 'English' }}
          </a>
          <a class="btn btn-primary header-cta" href="#lead" @click.prevent="scrollToElement('lead')">{{ t.navTalk }}</a>
        </nav>
      </div>
    </header>

    <nav class="booking-breadcrumb" :aria-label="currentLanguage === 'ar' ? 'مسار التنقل' : 'Breadcrumb'">
      <ol class="container">
        <li><router-link :to="{ name: 'home' }">{{ currentLanguage === 'ar' ? 'الأدوات' : 'Tools Hub' }}</router-link></li>
        <li aria-hidden="true">/</li>
        <li><router-link :to="{ name: 'home', query: { category: 'business' }, hash: '#tools' }">{{ currentLanguage === 'ar' ? 'أعمال' : 'Business' }}</router-link></li>
        <li aria-hidden="true">/</li>
        <li aria-current="page">{{ currentLanguage === 'ar' ? 'حاسبة خسائر الحجوزات' : 'Booking Loss Calculator' }}</li>
      </ol>
    </nav>

    <main>
      <section class="hero">
        <div class="container hero-grid">
          <div>
            <div class="eyebrow">
              <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path></svg>
              {{ t.eyebrow }}
            </div>
            <h1>{{ t.title1 }} <span style="color:#3a7afe;">{{ t.title2 }}</span></h1>
            <p>{{ t.subtitle }}</p>
            <div class="hero-actions">
              <a class="btn btn-primary" href="#calculator" @click.prevent="scrollToElement('calculator')">{{ t.startBtn }}</a>
            </div>
            <div class="trust-row">
              <div class="trust-chip">{{ t.trust1 }}</div>
              <div class="trust-chip">{{ t.trust2 }}</div>
              <div class="trust-chip">{{ t.trust3 }}</div>
            </div>
          </div>
          <div class="hero-visual">
            <div class="preview-card">
              <div class="preview-top">
                <span class="pill">{{ t.calcLabel }}</span>
              </div>
              <div class="loss-number">
                <small>{{ t.unutilizedLabel }}</small>
                <strong>45,000 ج.م</strong>
              </div>
              <div class="mini-list">
                <div class="mini-row"><span>{{ t.noShow }}</span><span style="color:#dc2626;">-12,000 ج.م</span></div>
                <div class="mini-row"><span>{{ t.cancel }}</span><span style="color:#dc2626;">-8,500 ج.م</span></div>
                <div class="mini-row"><span>{{ t.missed }}</span><span style="color:#dc2626;">-24,500 ج.م</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- The extracted component logic -->
      <BookingLossCalculatorCom />

      <section class="section" id="how">
        <div class="container">
          <div class="section-heading">
            <h2>{{ t.howTitle }}</h2>
            <p>{{ t.howDesc }}</p>
          </div>
          <div class="steps-grid">
            <div class="step-card">
              <div class="step-num">1</div>
              <h3>{{ t.how1Title }}</h3>
              <p>{{ t.how1Desc }}</p>
            </div>
            <div class="step-card">
              <div class="step-num">2</div>
              <h3>{{ t.how2Title }}</h3>
              <p>{{ t.how2Desc }}</p>
            </div>
            <div class="step-card">
              <div class="step-num">3</div>
              <h3>{{ t.how3Title }}</h3>
              <p>{{ t.how3Desc }}</p>
            </div>
            <div class="step-card">
              <div class="step-num">4</div>
              <h3>{{ t.how4Title }}</h3>
              <p>{{ t.how4Desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="section faq-section" id="faq">
        <div class="container">
          <div class="section-heading">
            <h2>{{ t.faqTitle }}</h2>
          </div>
          <div class="faq-list">
            <div class="faq-item">
              <h3>{{ t.faq1Q }}</h3>
              <p>{{ t.faq1A }}</p>
            </div>
            <div class="faq-item">
              <h3>{{ t.faq2Q }}</h3>
              <p>{{ t.faq2A }}</p>
            </div>
            <div class="faq-item">
              <h3>{{ t.faq3Q }}</h3>
              <p>{{ t.faq3A }}</p>
            </div>
            <div class="faq-item">
              <h3>{{ t.faq4Q }}</h3>
              <p>{{ t.faq4A }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import BookingLossCalculatorCom from '@/components/BookingLossCalculatorCom.vue';

export default {
  name: 'BookingLossCalculator',
  components: {
    BookingLossCalculatorCom
  },
  computed: {
    currentLanguage() {
      return this.$store.state.currentLanguage;
    },
    t() {
      if (this.currentLanguage === 'en') {
        return {
          eyebrow: "Free Evaluation Tool",
          title1: "Booking Loss",
          title2: "Calculator",
          subtitle: "A professional calculator to estimate the volume of lost bookings due to no-shows, uncompensated cancellations, missed calls, poor time distribution, and annual downtimes.",
          startBtn: "Start Calculation Now",
          trust1: "100% Free",
          trust2: "No Login Required",
          trust3: "Instant Results",
          calcLabel: "Booking Loss Calculator",
          navAria: "Main Links",
          navTool: "The Tool",
          navResult: "Results",
          navApps: "Booking Apps",
          navFaq: "FAQ",
          navTalk: "Talk to Us",
          unutilizedLabel: "Estimated Unutilized Opps",
          noShow: "No-Shows",
          cancel: "Uncompensated Cancels",
          missed: "Missed Calls",
          howTitle: "How Are Values Calculated?",
          howDesc: "We use a customized mathematical model based on capacity and service value metrics.",
          how1Title: "Data Entry",
          how1Desc: "You provide basic metrics about your business operations.",
          how2Title: "No-Shows & Cancels",
          how2Desc: "We calculate the direct impact of confirmed bookings that were not completed.",
          how3Title: "Lost Opportunities",
          how3Desc: "We analyze the impact of missed calls and poor time distribution on potential growth.",
          how4Title: "Smart Recommendations",
          how4Desc: "We present practical solutions tailored to the biggest source of unutilized opportunities in your business.",
          faqTitle: "Frequently Asked Questions",
          faq1Q: "What is the Booking Loss Calculator?",
          faq1A: "It's a free tool that helps service business owners estimate the unutilized financial opportunities resulting from no-shows, cancellations, missed calls, poor distribution, and downtimes.",
          faq2Q: "Are the financial results guaranteed?",
          faq2A: "No, the results are estimates based on the data you enter. The goal is to highlight areas for improvement, not to promise a guaranteed financial outcome.",
          faq3Q: "How does SplendApp help reduce booking losses?",
          faq3A: "SplendApp booking applications help organize appointments, send automated reminders, reduce no-shows, manage cancellations, suggest alternative times, and clarify availability.",
          faq4Q: "Is the tool free to use?",
          faq4A: "Yes, the tool is completely free, works within the browser, and requires no login."
        };
      }
      return {
        eyebrow: "أداة تقييم مجانية",
        title1: "حاسبة خسائر",
        title2: "الحجوزات وعدم الحضور",
        subtitle: "حاسبة احترافية لتقدير حجم الحجوزات الضائعة بسبب عدم الحضور، الإلغاءات غير المعوضة، المكالمات الفائتة، سوء توزيع الأوقات، والانقطاعات السنوية.",
        startBtn: "ابدأ الحساب الآن",
        trust1: "مجاني 100%",
        trust2: "لا يتطلب تسجيل دخول",
        trust3: "نتائج فورية",
        calcLabel: "حاسبة خسائر الحجوزات",
        navAria: "روابط رئيسية",
        navTool: "الأداة",
        navResult: "النتيجة",
        navApps: "تطبيقات الحجوزات",
        navFaq: "الأسئلة الشائعة",
        navTalk: "تحدث معنا",
        unutilizedLabel: "الفرص غير المستغلة (تقديري)",
        noShow: "عدم حضور (No-show)",
        cancel: "إلغاءات غير معوضة",
        missed: "مكالمات فائتة",
        howTitle: "كيف يتم حساب القيم؟",
        howDesc: "نعتمد على نموذج رياضي مخصص بناءً على معطيات الطاقة الاستيعابية وقيمة الخدمة.",
        how1Title: "إدخال المعطيات",
        how1Desc: "تقوم بتزويدنا بأرقام تقديرية لحجم عملك.",
        how2Title: "الغياب والإلغاء",
        how2Desc: "نحسب الأثر المباشر للحجوزات المؤكدة التي لم تتم.",
        how3Title: "الفرص الضائعة",
        how3Desc: "نحلل أثر المكالمات الضائعة وسوء التوزيع على النمو المحتمل.",
        how4Title: "توصيات ذكية",
        how4Desc: "نطرح حلولاً عملية مخصصة لأكبر مصدر للفرص غير المستغلة لديك.",
        faqTitle: "الأسئلة الشائعة",
        faq1Q: "ما هي حاسبة خسائر الحجوزات؟",
        faq1A: "هي أداة مجانية تساعد أصحاب الخدمات على تقدير الفرص المالية غير المستغلة الناتجة عن عدم حضور العملاء، الإلغاءات، فرص الحجز الفائتة، وسوء التوزيع.",
        faq2Q: "هل النتيجة المالية مؤكدة؟",
        faq2A: "لا. النتيجة تقديرية وتعتمد على البيانات التي يدخلها المستخدم، والهدف منها توضيح فرص التحسين.",
        faq3Q: "كيف تساعد سبلينداب في تقليل الخسائر؟",
        faq3A: "تطبيقات سبلينداب تساعد على تنظيم المواعيد، إرسال تذكيرات آلية لتقليل الغياب، وإدارة الإلغاءات بكفاءة.",
        faq4Q: "هل الأداة مجانية؟",
        faq4A: "نعم، الأداة مجانية بالكامل وتعمل داخل المتصفح بدون تسجيل."
      };
    }
  },
  methods: {
    toggleLanguage() {
      const newLang = this.currentLanguage === 'en' ? 'ar' : 'en';
      this.$store.commit('setCurrentLanguage', newLang);
      
      const routeName = newLang === 'en' ? 'booking-loss-calculator-en' : 'booking-loss-calculator-ar';
      if (this.$route.name !== routeName) {
        this.$router.push({ name: routeName });
      }
    },
    scrollToElement(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
    .booking-breadcrumb { background: rgba(255,255,255,0.9); border-bottom: 1px solid rgba(226,232,240,0.9); font-family: "Cairo", "DM Sans", sans-serif; }
    .booking-breadcrumb ol { display: flex; align-items: center; gap: 9px; min-height: 42px; margin-block: 0; padding-block: 7px; list-style: none; font-size: 14px; }
    .booking-breadcrumb a { color: #24365c; font-weight: 700; text-decoration: none; }
    .booking-breadcrumb a:hover { color: #3a7afe; }
    .site-header {
      position: sticky; top: 0; z-index: 50;
      background: rgba(255,255,255,0.86); backdrop-filter: blur(16px);
      border-bottom: 1px solid rgba(226, 232, 240, 0.8);
      font-family: "Cairo", "DM Sans", sans-serif;
    }
    .header-inner { min-height: 74px; display: flex; align-items: center; justify-content: space-between; gap: 18px; }
    .brand { display: flex; align-items: center; gap: 12px; font-weight: 900; letter-spacing: -0.02em; white-space: nowrap; text-decoration: none; color: #24365c; }
    .brand-mark {
      width: 42px; height: 42px; border-radius: 6px;
      background: linear-gradient(135deg, #3a7afe, #24365c);
      display: grid; place-items: center; color: #fff;
      box-shadow: 0 10px 22px rgba(40, 81, 217, 0.25);
    }
    .brand span:last-child { font-size: 1.04rem; }
    .nav { display: flex; align-items: center; gap: 24px; font-size: 0.98rem; font-weight: 700; }
    .nav a:not(.btn):not(.lang-toggle) { color: #64748b; text-decoration: none; transition: color 0.2s ease; }
    .nav a:not(.btn):not(.lang-toggle):hover { color: #3a7afe; }
    .lang-toggle { color: #3a7afe; font-weight: 800; text-decoration: none; border: 2px solid #edf3ff; background: #edf3ff; padding: 6px 14px; border-radius: 200px; transition: all 0.2s ease;}
    .lang-toggle:hover { background: #3a7afe; color: #fff; border-color: #3a7afe; }
    .header-cta { padding: 8px 18px !important; min-height: 40px !important; font-size: 0.9rem !important; }

    .hero { padding: 68px 0 44px; font-family: "Cairo", "DM Sans", sans-serif; background: radial-gradient(circle at top left, rgba(58, 122, 254, 0.08), transparent 40%), #F6F9FB; }
    .container { width: min(1180px, calc(100% - 32px)); margin: 0 auto; }
    .hero-grid { display: grid; grid-template-columns: minmax(0, 1.06fr) minmax(320px, 0.82fr); align-items: center; gap: 44px; }
    .eyebrow {
      display: inline-flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #e2e8f0;
      color: #3a7afe; font-weight: 800; border-radius: 200px; padding: 8px 14px;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05); margin-bottom: 20px;
    }
    h1 { font-size: clamp(1.95rem, 4.4vw, 3.85rem); line-height: 1.08; margin: 0 0 20px; letter-spacing: -0.05em; color: #24365c; }
    .hero p { font-size: clamp(0.98rem, 1.75vw, 1.14rem); color: #716c80; max-width: 670px; margin: 0 0 28px; line-height: 1.6;}
    .hero-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 26px; }
    .trust-row { display: flex; flex-wrap: wrap; gap: 10px; color: #716c80; font-weight: 700; font-size: 0.88rem; }
    .trust-chip { background: rgba(255,255,255,0.75); border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 200px; }
    .preview-card {
      background: #fff; border: 1px solid rgba(226, 232, 240, 0.9); border-radius: 12px;
      box-shadow: 0 18px 60px rgba(58, 122, 254, 0.12); padding: 22px; position: relative; overflow: hidden;
    }
    .preview-top { display: flex; align-items: center; justify-content: space-between; position: relative; z-index: 1; margin-bottom: 18px; }
    .pill { border-radius: 200px; background:#F6F9FB; color: #3a7afe; font-weight: 800; padding: 7px 11px; font-size: 0.84rem; display: inline-flex; }
    .loss-number { position: relative; z-index: 1; padding: 22px; border-radius: 8px; background: linear-gradient(135deg, #3a7afe, #24365c); color: #fff; margin-bottom: 16px; }
    .loss-number small { display: block; opacity: 0.8; font-weight: 700; margin-bottom: 8px; }
    .loss-number strong { display: block; font-size: clamp(2rem, 4vw, 3rem); line-height: 1; letter-spacing: -0.04em; }
    .mini-list { position: relative; z-index: 1; display: grid; gap: 10px; }
    .mini-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; background: #F6F9FB; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; font-weight: 800; }
    .mini-row span:first-child { color: #24365c; font-weight: 700; }

    .btn { border: 0; cursor: pointer; border-radius: 200px; padding: 13px 22px; font-weight: 800; font-size: 0.98rem; display: inline-flex; align-items: center; justify-content: center; transition: 0.2s ease; min-height: 48px; text-decoration: none; }
    .btn-primary { background: #3a7afe; color: #fff; box-shadow: 0 10px 20px rgba(58,122,254,0.2); }
    .btn-primary:hover { transform: translateY(-2px); }

    .section { padding: 50px 0; font-family: "Cairo", "DM Sans", sans-serif; background: #fff;}
    .section-heading { text-align: center; margin-bottom: 30px; }
    .section-heading h2 { font-size: 2rem; color: #24365c; margin-bottom: 10px;}
    .section-heading p { color: #716c80; font-size: 1.1rem; }

    .steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
    .step-card { background: #F6F9FB; padding: 25px; border-radius: 8px; border: 1px solid #e2e8f0; text-align: center; }
    .step-num { width: 40px; height: 40px; background: #3a7afe; color: #fff; display: flex; align-items: center; justify-content: center; border-radius: 50%; font-size: 1.2rem; font-weight: bold; margin: 0 auto 15px; }
    .step-card h3 { color: #24365c; margin-bottom: 10px; font-size: 1.2rem; }
    .step-card p { color: #716c80; font-size: 0.95rem; }

    .faq-section { background: #F6F9FB; }
    .faq-list { max-width: 800px; margin: 0 auto; display: grid; gap: 15px; }
    .faq-item { background: #fff; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; }
    .faq-item h3 { color: #24365c; margin-bottom: 10px; font-size: 1.1rem; }
    .faq-item p { color: #716c80; font-size: 0.95rem; margin: 0; line-height: 1.6; }

    @media (max-width: 950px) {
      .header-inner {
        min-height: auto;
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px 0;
      }
      .brand {
        justify-content: center;
      }
      .brand img {
        width: min(220px, 62vw);
        height: auto;
      }
      .nav {
        width: 100%;
        overflow-x: auto;
        gap: 8px;
        padding-bottom: 2px;
        scrollbar-width: none;
      }
      .nav::-webkit-scrollbar {
        display: none;
      }
      .nav a:not(.btn):not(.lang-toggle),
      .lang-toggle,
      .header-cta {
        flex: 0 0 auto;
        white-space: nowrap;
      }
      .nav a:not(.btn):not(.lang-toggle) {
        padding: 8px 12px;
        border: 1px solid #e2e8f0;
        border-radius: 200px;
        background: #fff;
        font-size: 0.9rem;
      }
      .lang-toggle {
        padding: 8px 14px;
      }
      .header-cta {
        padding: 8px 16px !important;
      }
      .hero-grid { grid-template-columns: 1fr; }
      .steps-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 600px) {

      .container {
        width: min(100% - 24px, 1180px);
      }
      .nav {
        font-size: 0.82rem;
      }
      .hero {
        padding: 40px 0 30px;
      }
      .hero-visual {
        display: none;
      }
      h1 {
        font-size: 2rem;
        line-height: 1.15;
        letter-spacing: 0;
      }
      .hero p {
        font-size: 1rem;
      }
      .hero-actions .btn {
        width: 100%;
      }
      .steps-grid { grid-template-columns: 1fr; }
    }

    #how{
      background-color: #F6F9FB;
    }
</style>
